
// comparedly-frontend/src/layouts/social-stash/SocialStashModal.js
import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import FacebookSocialCard from "./components/FacebookSocialCard";
import YouTubeSocialCard from "./components/YouTubeSocialCard";
import InstagramSocialCard from "./components/InstagramSocialCard";
import SocialPopup from "./components/SocialPopup";
import ProfileService from "services/profile-service";
import WorkspaceService from "services/workspace-service";
import ProfileSearch from "./components/toolbar/ProfileSearch";

const formatCount = (count) => {
  if (count >= 1_000_000_000) return `${(count / 1_000_000_000).toFixed(1)}B`;
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return count.toString();
};

import Snackbar from "@mui/material/Snackbar";

// 🚀 UPDATED: Added initialSelectedProfiles prop
function SocialStashModal({ onConfirmSelection, confirmText = "Add Selected", initialSelectedProfiles = [] }) {
  const [facebookProfiles, setFacebookProfiles] = useState([]);
  const [youtubeProfiles, setYouTubeProfiles] = useState([]);
  const [instagramProfiles, setInstagramProfiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // 🚀 Initialize state with passed profiles
  const [selectedProfiles, setSelectedProfiles] = useState(initialSelectedProfiles);
  const [loading, setLoading] = useState(true);

  // ✅ Snackbar state for error messages
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const MAX_SELECTION = 4;

  // 🚀 SYNC: Update internal selection when the prop changes (e.g. when modal opens with existing selection)
  // 🚀 SYNC: Initialize selection on mount. 
  // ⚠️ CRITICAL: Must be [] (mount only). If we depend on [initialSelectedProfiles], 
  // it causes infinite resets because the parent passes a new array reference on every render.
  useEffect(() => {
    if (initialSelectedProfiles && initialSelectedProfiles.length > 0) {
      setSelectedProfiles(initialSelectedProfiles);
    }
  }, []);

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        setLoading(true);
        const workspaceProfiles = await WorkspaceService.fetchWorkspaceProfiles();
        const ownMap = {};
        const createdAtMap = {};

        workspaceProfiles.forEach((p) => {
          ownMap[p.id] = p.own === true;
          createdAtMap[p.id] = p.createdAt;
        });

        const getIds = (platform) =>
          workspaceProfiles.filter((p) => p.platform === platform).map((p) => p.id);

        const fetchAndEnrich = async (platform, ids) => {
          if (ids.length === 0) return [];
          const profiles = await ProfileService.fetchProfile(platform, ids).catch(() => []);
          return profiles.map((p) => ({
            ...p,
            own: ownMap[p.id] || false,
            createdAt: createdAtMap[p.id] || null,
          })).sort((a, b) => {
            if (a.own === b.own) {
              return new Date(b.createdAt) - new Date(a.createdAt);
            }
            return a.own ? -1 : 1;
          });
        };

        const [fb, yt, ig] = await Promise.all([
          fetchAndEnrich("Facebook", getIds("Facebook")),
          fetchAndEnrich("YouTube", getIds("YouTube")),
          fetchAndEnrich("Instagram", getIds("Instagram"))
        ]);

        setFacebookProfiles(fb);
        setYouTubeProfiles(yt);
        setInstagramProfiles(ig);
      } catch (e) {
        console.error("Error fetching stash profiles:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchProfiles();
  }, []);

  const filterProfiles = (profiles) =>
    profiles.filter((p) => p.name.toLowerCase().includes(searchQuery.toLowerCase()));

  const handleProfileSelect = (profileId, name, pic, link, platform, own) => {
    setSelectedProfiles((prev) => {
      const exists = prev.some((p) => p.id === profileId && p.platform === platform);
      return exists
        ? prev.filter((p) => !(p.id === profileId && p.platform === platform))
        : [...prev, { id: profileId, platform, name, profilePictureUrl: pic, link, own }];
    });
  };

  const clearSelection = () => setSelectedProfiles([]);

  const handleDeleteProfiles = async () => {
    if (selectedProfiles.length === 0) return;
    try {
      const grouped = selectedProfiles.reduce((acc, p) => {
        if (!acc[p.platform]) acc[p.platform] = [];
        acc[p.platform].push(p.id);
        return acc;
      }, {});
      for (const [platform, ids] of Object.entries(grouped)) {
        await WorkspaceService.deleteWorkspaceProfiles(ids, platform);
      }
      // Optimistic updates...
      setFacebookProfiles(prev => prev.filter(p => !selectedProfiles.some(sp => sp.id === p.id && sp.platform === "Facebook")));
      setYouTubeProfiles(prev => prev.filter(p => !selectedProfiles.some(sp => sp.id === p.id && sp.platform === "YouTube")));
      setInstagramProfiles(prev => prev.filter(p => !selectedProfiles.some(sp => sp.id === p.id && sp.platform === "Instagram")));
      setSelectedProfiles([]);
    } catch (e) {
      console.error("Error deleting profiles:", e);
    }
  };

  return (
    <MDBox p={2}>
      <MDBox pt={0} pb={4}>
        <MDBox
          sx={{
            position: 'sticky',
            top: -16,
            zIndex: 100,
            backgroundColor: 'background.paper',
            paddingTop: 2,
            marginTop: -2
          }}
        >
          <ProfileSearch searchQuery={searchQuery} setSearchQuery={setSearchQuery} showTitle={false} />
        </MDBox>
        <Grid container spacing={4} mt={1}>
          <Grid item xs={12}>
            <FacebookSocialCard
              profiles={filterProfiles(facebookProfiles)}
              loading={loading}
              selectedProfiles={selectedProfiles}
              formatCount={formatCount}
              onProfileSelect={(id, name, pic, link) =>
                handleProfileSelect(id, name, pic, link, "Facebook", facebookProfiles.find((p) => p.id === id)?.own)
              }
            />
          </Grid>
          <Grid item xs={12}>
            <YouTubeSocialCard
              profiles={filterProfiles(youtubeProfiles)}
              loading={loading}
              selectedProfiles={selectedProfiles}
              formatCount={formatCount}
              onProfileSelect={(id, name, pic, link) =>
                handleProfileSelect(id, name, pic, link, "YouTube", youtubeProfiles.find((p) => p.id === id)?.own)
              }
            />
          </Grid>
          <Grid item xs={12}>
            <InstagramSocialCard
              profiles={filterProfiles(instagramProfiles)}
              loading={loading}
              selectedProfiles={selectedProfiles}
              formatCount={formatCount}
              onProfileSelect={(id, name, pic, link) =>
                handleProfileSelect(id, name, pic, link, "Instagram", instagramProfiles.find((p) => p.id === id)?.own)
              }
            />
          </Grid>
        </Grid>
      </MDBox>

      {onConfirmSelection ? (
        <MDBox
          display="flex"
          justifyContent="flex-end"
          p={2}
          sx={{ position: 'sticky', bottom: 0, bgcolor: 'background.paper', borderTop: '1px solid #ddd', zIndex: 10 }}
        >
          <MDButton
            variant="contained"
            color="accent"
            disabled={selectedProfiles.length === 0}
            onClick={() => {
              if (selectedProfiles.length > MAX_SELECTION) {
                setSnackbar({ open: true, message: `⚠️ You can select up to ${MAX_SELECTION} profiles only.` });
                return;
              }
              onConfirmSelection(selectedProfiles);
            }}
          >
            {confirmText} ({selectedProfiles.length})
          </MDButton>
        </MDBox>
      ) : (
        <SocialPopup
          selectedProfiles={selectedProfiles}
          onDeleteConfirm={handleDeleteProfiles}
          onCancel={clearSelection}
          enableReportGeneration={false}
        />
      )}

      {/* ✅ Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        ContentProps={{
          sx: { borderRadius: 2, boxShadow: 3, mb: 6 },
        }}
      />
    </MDBox>
  );
}

export default SocialStashModal;